import * as React from 'react';
import ContentEditable from 'react-contenteditable';
import Textarea from 'react-textarea-autosize';
import _ from 'underscore';

type Surface = 'contenteditable' | 'textarea';

interface AnswerTextEditorProps {
  errorTypes: string[];
  generateStyleObjects: (targetString: string, userString: string, flexible?: boolean) => any;
  onChange: (value: string, extra?: any) => void;
  surface: Surface;
  value: string;
  blockPaste?: boolean;
  disabled?: boolean;
  editorIndex?: number;
  getResponse?: (id: string) => any;
  hasError?: boolean;
  latestAttempt?: any;
  onSubmitResponse?: () => void;
  placeholder?: string;
  spellCheck?: boolean;
}

export class AnswerTextEditor extends React.Component<AnswerTextEditorProps> {
  static defaultProps = { blockPaste: true }

  componentDidMount() {
    const { blockPaste, } = this.props
    if (blockPaste) {
      window.addEventListener('paste', this.preventPaste, true);
    }
  }

  componentDidUpdate(prevProps) {
    const { latestAttempt, getResponse, } = this.props
    if (latestAttempt !== prevProps.latestAttempt) {
      if (latestAttempt && latestAttempt.found) {
        const parentID = latestAttempt.response.parentID;
        const errorKeys = _.keys(this.getErrorsForAttempt(latestAttempt));
        const nErrors = errorKeys.length;
        let targetText;
        if (parentID) {
          const parentResponse = getResponse(parentID);
          targetText = parentResponse.text;
          const newStyle = this.getUnderliningFunctionFromAuthor(latestAttempt.response.author, targetText, latestAttempt.submitted);
          if (newStyle) {
            this.applyNewStyle(newStyle);
          }
          return;
        } else if (nErrors > 0) {
          targetText = latestAttempt.response.text;
        } else {
          this.clearStyle();
          return;
        }
        const newStyle = this.getUnderliningFunction(errorKeys[0], targetText, latestAttempt.submitted);
        if (newStyle) {
          this.applyNewStyle(newStyle);
        }
      }
    }
  }

  componentWillUnmount() {
    window.removeEventListener('paste', this.preventPaste, true);
  }

  answerBox: any;

  preventPaste = (e) => {
    e.preventDefault()
    return false
  }

  getErrorsForAttempt(attempt) {
    const { errorTypes, } = this.props
    return _.pick(attempt, ...errorTypes);
  }

  getUnderliningFunction(errorType, targetString, userString) {
    const { generateStyleObjects, } = this.props
    switch (errorType) {
      case 'punctuationError':
      case 'typingError':
      case 'caseError':
      case 'modifiedWordError':
      case 'additionalWordError':
      case 'missingWordError':
        return generateStyleObjects(targetString, userString);
      case 'flexibleModifiedWordError':
      case 'flexibleAdditionalWordError':
      case 'flexibleMissingWordError':
        return generateStyleObjects(targetString, userString, true);
      default:
        return undefined;
    }
  }

  getUnderliningFunctionFromAuthor(author, targetString, userString) {
    const { generateStyleObjects, } = this.props
    switch (author) {
      case 'Punctuation Hint':
      case 'Capitalization Hint':
      case 'Modified Word Hint':
      case 'Additional Word Hint':
      case 'Missing Word Hint':
        return generateStyleObjects(targetString, userString);
      case 'Flexible Modified Word Hint':
      case 'Flexible Additional Word Hint':
      case 'Flexible Missing Word Hint':
        return generateStyleObjects(targetString, userString, true);
      default:
        return undefined;
    }
  }

  setAnswerBoxRef = (node) => { this.answerBox = node }

  applyNewStyle(newStyle) {
    if (newStyle.inlineStyleRanges[0]) {
      const offset = newStyle.inlineStyleRanges[0].offset;
      const end = offset + newStyle.inlineStyleRanges[0].length;
      const input = this.answerBox;
      input.selectionStart = offset;
      input.selectionEnd = end;
    }
  }

  clearStyle() {
    const input = this.answerBox;
    input.selectionStart = 0;
    input.selectionEnd = 0;
  }

  displayedHTML() {
    const { value, latestAttempt, } = this.props
    if (!(latestAttempt && latestAttempt.response && latestAttempt.response.misspelled_words)) {
      return value
    }
    const wordArray = value.split(' ')
    const newWordArray = wordArray.map(word => {
      const punctuationStrippedWord = word.replace(/[^A-Za-z0-9\s]/g, '')
      if (latestAttempt.response.misspelled_words.includes(punctuationStrippedWord)) {
        return `<b>${word}</b>`
      } else {
        return word
      }
    })

    return newWordArray.join(' ')
  }

  handleKeyDown = (e) => {
    const { disabled, onSubmitResponse, } = this.props
    if (disabled || e.key !== 'Enter') { return }

    e.preventDefault();
    onSubmitResponse();
  }

  handleTextChange = (e) => {
    const { disabled, onChange, surface, editorIndex, } = this.props
    if (disabled) { return }

    if (surface === 'contenteditable') {
      const stripHTML = e.target.value.replace(/<\/?[^>]+(>|$)/g, '').replace(/&nbsp;/g, ' ')
      onChange(stripHTML, this.answerBox);
    } else {
      onChange(e.target.value, editorIndex);
    }
  }

  handleDrop = (e) => e.preventDefault()

  render() {
    const { surface, hasError, disabled, value, spellCheck, placeholder, } = this.props
    return (
      <div className={`student text-editor card is-fullwidth ${hasError ? 'error' : ''} ${disabled ? 'disabled-editor' : ''}`}>
        <div className="card-content">
          <div className="content">
            {surface === 'contenteditable'
              ? (
                <ContentEditable
                  className="connect-text-area"
                  data-gramm={false}
                  disabled={disabled}
                  html={this.displayedHTML()}
                  innerRef={this.setAnswerBoxRef}
                  onChange={this.handleTextChange}
                  onDrop={this.handleDrop}
                  onKeyDown={this.handleKeyDown}
                  placeholder={placeholder}
                  spellCheck={false}
                />
              )
              : (
                <Textarea
                  autoCapitalize="off"
                  autoCorrect="off"
                  className="connect-text-area"
                  onDrop={this.handleDrop}
                  onInput={this.handleTextChange}
                  onKeyDown={this.handleKeyDown}
                  placeholder={placeholder}
                  ref={this.setAnswerBoxRef}
                  spellCheck={spellCheck || false}
                  value={value}
                />
              )}
          </div>
        </div>
      </div>
    );
  }
}
